import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef } from 'react'

import { DatoStructuredText } from '@/features/common/'
import { GatsbyImageFocused } from '@/features/common/'
import { DatoLink } from '@/features/dato-link'
import { aspectRatio, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentPropsWithoutRef<'section'> {
  heading?: string | null
  data?: readonly (Queries.BeyondTheClassroomFragment | null)[] | null
}

export const BeyondTheClassroom = ({
  heading,
  data,
  ...props
}: Props): JSX.Element => {
  const styles = {
    section: css`
      padding: var(--row-108) var(--margin);
      background: ${colors.gray95};
      ${mq().s} {
        padding: var(--row-108) calc(var(--margin) / 2);
      }
    `,
    heading: css`
      font-size: var(--fs-60);
      color: ${colors.gray10};
      display: flex;
      align-items: flex-end;
      gap: 0.25em;
      margin: 0 0 0.75em;
      ${mq().s} {
        display: grid;
        margin: 0 calc(var(--margin) / 2) 0.25em;
      }
      &:after {
        display: block;
        content: '';
        flex: 1;
        width: 100%;
        height: 2px;
        background: ${colors.gray75};
        margin-bottom: 0.25em;
        ${mq().s} {
          /* display: none; */
        }
      }
    `,
    blocks: css`
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: var(--gtr-24);
      ${mq().m} {
        grid-template-columns: 1fr;
      }
    `,
    block: css`
      border: 1rem solid ${colors.gray90};
      background: #fff;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 2em;
      ${mq().s} {
        grid-template-columns: 1fr;
        border-width: calc(var(--margin) / 2);
      }
    `,
    blockText: css`
      align-self: center;
      padding: 2em 2em 2em 0;
      min-width: 25ch;
      h3 {
        font-size: var(--fs-36);
        margin: 0 0 0.5em;
        &:after {
          content: '';
          display: block;
          width: 1em;
          margin-top: 0.5em;
          height: 2px;
          background: ${colors.red};
        }
      }
      a {
        font-size: var(--fs-15);
        font-weight: 500;
        color: ${colors.red};
        @media (hover: hover) {
          &:hover {
            color: ${colors.redDark};
          }
        }
      }
      div:nth-of-type(4n - 1) > &,
      div:nth-of-type(4n) > & {
        padding: 2em 0 2em 2em;
      }
      ${mq().m} {
        div:nth-of-type(odd) > & {
          padding: 2em 2em 2em 0;
          order: 2;
        }
        div:nth-of-type(even) > & {
          padding: 2em 0 2em 2em;
          order: 1;
        }
      }
      ${mq().s} {
        div:nth-of-type(n) > & {
          padding: 1.5em 1em 1em;
          order: 2;
        }
      }
    `,
    blockImage: css`
      min-height: 100%;
      div:nth-of-type(4n - 1) > &,
      div:nth-of-type(4n) > & {
        order: 2;
      }
      ${mq().m} {
        div:nth-of-type(odd) > & {
          order: 1;
        }
        div:nth-of-type(even) > & {
          order: 2;
        }
      }
      ${mq().s} {
        ${aspectRatio(1.667)};
        div:nth-of-type(n) > & {
          order: 1;
        }
      }
    `,
  }
  return (
    <section
      css={styles.section}
      {...props}
    >
      <h2 css={styles.heading}>
        <span>{heading}</span>
      </h2>
      <div css={styles.blocks}>
        {data?.map((block, i) => (
          <div
            key={i}
            css={styles.block}
          >
            <GatsbyImageFocused
              css={styles.blockImage}
              image={block?.image?.gatsbyImageData}
              aspectRatio={1}
              data={block?.image}
            />
            <div css={styles.blockText}>
              <h3>{block?.heading}</h3>
              <DatoStructuredText data={block?.body} />
              {block?.links?.map((link, i) => (
                <DatoLink
                  key={i}
                  data={link}
                  showIcon
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export const BeyondTheClassroomFragments = graphql`
  fragment BeyondTheClassroom on DatoCmsBeyondTheClassroom {
    __typename
    id: originalId
    heading
    body {
      value
    }
    links {
      ...PageLink
    }
    image {
      gatsbyImageData(
        width: 480
        sizes: "(max-width: 540px) 100vw, (max-width: 900px) 50vw, 25vw"
        imgixParams: { ar: "1:1", fit: "crop", crop: "focalpoint" }
      )
      ...ImageFocalData
    }
  }
`
